import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { TicketList, TicketThread } from "@/pages/SupportPage";

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="lux-eyebrow mb-2 block">{label}</span>
    <input data-testid={testId} className="lux-input" {...props} />
  </label>
);

const useSave = () => {
  const [busy, setBusy] = useState(false);
  const save = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, save];
};

const FEE_FIELDS = [["wait_rate", "Wait rate per minute ($)"], ["free_wait_minutes", "Free wait minutes"], ["cancel_fee", "Cancel fee ($)"], ["toll_per_leg", "Toll fee per leg ($)"]];

export const FeesSettings = () => {
  const [form, setForm] = useState(null);
  const [busy, save] = useSave();
  useEffect(() => {
    let alive = true;
    api.get("/admin/fees").then(({ data }) => alive && setForm((f) => f || Object.fromEntries(FEE_FIELDS.map(([k]) => [k, String(data[k])]))));
    return () => { alive = false; };
  }, []);
  if (!form) return null;
  const submit = (e) => { e.preventDefault(); save(() => api.put("/admin/fees", Object.fromEntries(FEE_FIELDS.map(([k]) => [k, Number(form[k]) || 0]))), "Fees saved"); };
  return (
    <form onSubmit={submit} className="lux-card grid gap-5 p-6 sm:grid-cols-2" data-testid="fees-form">
      {FEE_FIELDS.map(([k, l]) => (
        <Field key={k} label={l} testId={`fees-${k}-input`} type="number" min="0" step="0.01" value={form[k]} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); }} />
      ))}
      <p className="text-xs text-zinc-500 sm:col-span-2">Wait time is charged per started minute beyond the free minutes, from Arrived until Start Trip. The cancel fee applies when the driver has covered more than 50% of the way to pickup.</p>
      <button type="submit" disabled={busy} data-testid="fees-save-button" className="btn-gold sm:w-fit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save fees</button>
    </form>
  );
};

export const DiscountTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ min_trips: "", percent: "" });
  const [busy, save] = useSave();
  const load = useCallback(() => api.get("/admin/discount-tiers").then(({ data }) => setTiers(data)), []);
  useEffect(() => { load(); }, [load]);
  const update = async (t, patch) => { await save(() => api.put(`/admin/discount-tiers/${t.id}`, { min_trips: t.min_trips, percent: t.percent, active: t.active, ...patch }), "Tier updated"); load(); };
  const add = async (e) => {
    e.preventDefault();
    await save(() => api.post("/admin/discount-tiers", { min_trips: Number(form.min_trips), percent: Number(form.percent), active: true }), "Tier added");
    setForm({ min_trips: "", percent: "" });
    load();
  };
  return (
    <div className="space-y-4" data-testid="discount-tiers">
      {tiers.map((t) => <TierRow key={t.id} tier={t} onSave={update} onDelete={async () => { await save(() => api.delete(`/admin/discount-tiers/${t.id}`)); load(); }} />)}
      <form onSubmit={add} className="lux-card flex flex-wrap items-end gap-3 p-5">
        <div className="w-32"><Field label="Min trips" testId="tier-new-min-input" type="number" min="1" required value={form.min_trips} onChange={(e) => setForm({ ...form, min_trips: e.target.value })} /></div>
        <div className="w-32"><Field label="Percent" testId="tier-new-percent-input" type="number" min="0.1" step="0.1" required value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} /></div>
        <button type="submit" disabled={busy} data-testid="tier-add-button" className="btn-gold !py-3">Add tier</button>
      </form>
    </div>
  );
};

const TierRow = ({ tier, onSave, onDelete }) => {
  const [pct, setPct] = useState(String(tier.percent));
  const [min, setMin] = useState(String(tier.min_trips));
  return (
    <div data-testid={`tier-row-${tier.min_trips}`} className="lux-card flex flex-wrap items-end gap-3 p-5">
      <div className="w-32"><Field label="Min trips" testId={`tier-min-input-${tier.min_trips}`} type="number" min="1" value={min} onChange={(e) => setMin(e.target.value)} /></div>
      <div className="w-32"><Field label="Percent" testId={`tier-percent-input-${tier.min_trips}`} type="number" min="0.1" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} /></div>
      <button data-testid={`tier-save-${tier.min_trips}`} onClick={() => onSave(tier, { min_trips: Number(min), percent: Number(pct) })} className="btn-gold !py-3">Save</button>
      <button data-testid={`tier-toggle-${tier.min_trips}`} onClick={() => onSave(tier, { active: !tier.active })} className="btn-outline-gold !py-3">{tier.active ? "Disable" : "Enable"}</button>
      <button data-testid={`tier-delete-${tier.min_trips}`} onClick={onDelete} className="p-3 text-zinc-500 hover:text-red-300" aria-label="Delete tier"><Trash2 className="h-4 w-4" /></button>
      {!tier.active && <span className="text-xs text-zinc-500">Disabled</span>}
    </div>
  );
};

const emptySpecial = () => ({ title: "", description: "", code: "", discount_type: "percent", value: "", expires_at: "", target: "all", customer_ids: [] });

export const SpecialsAdmin = ({ customers }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptySpecial());
  const [busy, save] = useSave();
  const load = useCallback(() => api.get("/admin/specials").then(({ data }) => setList(data)), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleCustomer = (id) => setForm((f) => ({ ...f, customer_ids: f.customer_ids.includes(id) ? f.customer_ids.filter((x) => x !== id) : [...f.customer_ids, id] }));
  const submit = async (e) => {
    e.preventDefault();
    const r = await save(() => api.post("/admin/specials", { ...form, value: Number(form.value), expires_at: new Date(form.expires_at).toISOString() }), "Special sent");
    if (r) { setForm(emptySpecial()); load(); }
  };
  const toggle = async (s) => { await save(() => api.put(`/admin/specials/${s.id}`, { ...s, active: !s.active })); load(); };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={submit} className="lux-card space-y-4 p-6" data-testid="special-form">
        <p className="lux-eyebrow">New special</p>
        <Field label="Title" testId="special-title-input" required value={form.title} onChange={set("title")} />
        <Field label="Description" testId="special-description-input" value={form.description} onChange={set("description")} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" testId="special-code-input" required value={form.code} onChange={set("code")} />
          <Field label="Expires" testId="special-expiry-input" type="datetime-local" required value={form.expires_at} onChange={set("expires_at")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="lux-eyebrow mb-2 block">Type</span>
            <select data-testid="special-type-select" className="lux-input" value={form.discount_type} onChange={set("discount_type")}><option value="percent">% off</option><option value="amount">$ off</option></select></label>
          <Field label="Value" testId="special-value-input" type="number" min="0.01" step="0.01" required value={form.value} onChange={set("value")} />
        </div>
        <label className="block"><span className="lux-eyebrow mb-2 block">Send to</span>
          <select data-testid="special-target-select" className="lux-input" value={form.target} onChange={set("target")}><option value="all">All customers</option><option value="selected">Selected customers</option></select></label>
        {form.target === "selected" && (
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-white/10 p-3" data-testid="special-customer-list">
            {customers.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" data-testid={`special-customer-${c.email}`} checked={form.customer_ids.includes(c.id)} onChange={() => toggleCustomer(c.id)} /> {c.name} · {c.email}
              </label>
            ))}
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="special-submit-button" className="btn-gold">Create & send</button>
      </form>
      <div className="space-y-3" data-testid="specials-list">
        {list.map((s) => (
          <div key={s.id} data-testid={`special-row-${s.code}`} className={`lux-card p-5 ${!s.active || s.expired ? "opacity-60" : ""}`}>
            <div className="flex items-center justify-between gap-2"><p className="font-mono text-amber-300">{s.code}</p><p className="text-amber-100">{s.discount_type === "percent" ? `${s.value}%` : money(s.value)} off</p></div>
            <p className="mt-1 text-sm text-zinc-200">{s.title}</p>
            <p className="mt-1 text-xs text-zinc-500">{s.target === "all" ? "All customers" : `${s.customer_ids.length} selected`} · {s.expired ? "Expired" : "Expires"} {fmtDate(s.expires_at)}</p>
            <button data-testid={`special-toggle-${s.code}`} onClick={() => toggle(s)} className="mt-3 text-xs text-amber-300 hover:text-amber-200">{s.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DriverPromosAdmin = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", details: "", start_date: "", end_date: "" });
  const [busy, save] = useSave();
  const load = useCallback(() => api.get("/admin/driver-promos").then(({ data }) => setList(data)), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (await save(() => api.post("/admin/driver-promos", { ...form, active: true }), "Driver promo posted")) { setForm({ title: "", details: "", start_date: "", end_date: "" }); load(); }
  };
  const toggle = async (p) => { await save(() => api.put(`/admin/driver-promos/${p.id}`, { ...p, active: !p.active })); load(); };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={submit} className="lux-card space-y-4 p-6" data-testid="driver-promo-form">
        <p className="lux-eyebrow">New driver promo</p>
        <Field label="Title" testId="driver-promo-title-input" required value={form.title} onChange={set("title")} />
        <label className="block"><span className="lux-eyebrow mb-2 block">Details</span><textarea data-testid="driver-promo-details-input" required className="lux-input min-h-[90px]" value={form.details} onChange={set("details")} /></label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start" testId="driver-promo-start-input" type="date" required value={form.start_date} onChange={set("start_date")} />
          <Field label="End" testId="driver-promo-end-input" type="date" required value={form.end_date} onChange={set("end_date")} />
        </div>
        <button type="submit" disabled={busy} data-testid="driver-promo-submit-button" className="btn-gold">Post promo</button>
      </form>
      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} data-testid="admin-driver-promo-row" className={`lux-card p-5 ${p.active ? "" : "opacity-60"}`}>
            <p className="text-amber-100">{p.title}</p><p className="mt-1 text-sm text-zinc-400">{p.details}</p>
            <p className="mt-1 text-xs text-zinc-500">{p.start_date} → {p.end_date}</p>
            <button onClick={() => toggle(p)} className="mt-3 text-xs text-amber-300">{p.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SupportAdmin = () => {
  const [tickets, setTickets] = useState([]);
  const [current, setCurrent] = useState(null);
  const load = useCallback(async () => setTickets((await api.get("/tickets")).data), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr]" data-testid="admin-support">
      <TicketList tickets={tickets} selected={current?.id} onSelect={setCurrent} />
      {current ? <TicketThread ticket={current} admin onChange={(t) => { setCurrent(t); load(); }} /> : <p className="lux-card p-8 text-sm text-zinc-500">Select a ticket.</p>}
    </div>
  );
};
